import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["Inquiry", "Qualified Lead", "Consultation Scheduled", "Proposal Sent", "Proposal Accepted", "Closed Lost"];

export const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data.leads)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status });
      toast.success(`Lead moved to ${status}`);
      load();
      if (selected?.id === lead.id) setSelected({ ...selected, status });
    } catch { toast.error("Update failed"); }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Leads</div>
        <h1 className="text-3xl font-bold tracking-tight">Consultation requests.</h1>
      </div>
      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table fx-rows-interactive" data-testid="admin-leads-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Product</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={5} className="p-0">
                <div className="fx-empty" style={{ border: "none", borderRadius: 0 }}>
                  <div className="fx-empty-icon">📥</div>
                  <h4>No leads yet</h4>
                  <p>Consultation requests from the public site will appear here as soon as visitors submit the contact form or book a call.</p>
                </div>
              </td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                <td>
                  <div className="font-semibold">{l.full_name}</div>
                  <div className="text-xs text-[var(--ft-muted)]">{l.email}</div>
                </td>
                <td>{l.company || "—"}</td>
                <td>{l.product_interest}</td>
                <td><span className="ft-badge">{l.status}</span></td>
                <td className="text-right"><span className="text-[var(--ft-gold)] text-sm font-semibold">Open →</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-modal">
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="ft-eyebrow">Lead</div>
                <h2 className="text-2xl font-bold mt-2">{selected.full_name}</h2>
                <div className="text-sm text-[var(--ft-muted)]">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[var(--ft-muted)] hover:text-black">✕</button>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="text-[var(--ft-muted)]">Company:</span> {selected.company || "—"}</div>
              <div><span className="text-[var(--ft-muted)]">Phone:</span> {selected.phone || "—"}</div>
              <div><span className="text-[var(--ft-muted)]">Product Interest:</span> {selected.product_interest}</div>
              <div><span className="text-[var(--ft-muted)]">Volume:</span> {selected.order_volume || "—"}</div>
              <div><span className="text-[var(--ft-muted)]">Timeline:</span> {selected.timeline || "—"}</div>
              {selected.message && <div><span className="text-[var(--ft-muted)]">Message:</span> {selected.message}</div>}
              {(selected.first_touch_brand || selected.last_touch_brand) && (
                <div className="mt-4 pt-3 border-t border-[var(--ft-border)] space-y-1.5">
                  <div className="ft-eyebrow">Attribution</div>
                  {selected.first_touch_brand && <div><span className="text-[var(--ft-muted)]">First touch:</span> <span className="font-semibold">{selected.first_touch_brand}</span></div>}
                  {selected.last_touch_brand && <div><span className="text-[var(--ft-muted)]">Last touch:</span> <span className="font-semibold">{selected.last_touch_brand}</span></div>}
                  {selected.source_page && <div><span className="text-[var(--ft-muted)]">Source page:</span> {selected.source_page}</div>}
                  {selected.cta_clicked && <div><span className="text-[var(--ft-muted)]">CTA:</span> {selected.cta_clicked}</div>}
                  {(selected.utm_source || selected.utm_campaign) && (
                    <div><span className="text-[var(--ft-muted)]">UTM:</span> {selected.utm_source}/{selected.utm_medium || "-"}/{selected.utm_campaign || "-"}</div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 pt-5 border-t border-[var(--ft-border)]">
              <div className="ft-label">Update Status</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected, s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      selected.status === s
                        ? "bg-[var(--ft-black)] text-white border-[var(--ft-black)]"
                        : "border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]"
                    }`}
                    data-testid={`lead-status-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export const AdminClients = () => {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data.clients)).catch(() => {}); }, []);
  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Clients</div>
        <h1 className="text-3xl font-bold tracking-tight">Active client accounts.</h1>
      </div>
      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table fx-rows-interactive" data-testid="admin-clients-table">
          <thead>
            <tr><th>Name</th><th>Company</th><th>Email</th><th>Phone</th></tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={4} className="p-0">
                <div className="fx-empty" style={{ border: "none", borderRadius: 0 }}>
                  <div className="fx-empty-icon">👥</div>
                  <h4>No clients yet</h4>
                  <p>Once a lead converts and a project is opened, the client account will appear here for quick access.</p>
                </div>
              </td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id}>
                <td className="font-semibold">{c.full_name}</td>
                <td>{c.company || "—"}</td>
                <td>{c.email}</td>
                <td>{c.phone || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export const AdminContacts = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then((r) => setItems(r.data.contacts)).catch(() => {}); }, []);
  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Contact Requests</div>
        <h1 className="text-3xl font-bold tracking-tight">General inquiries.</h1>
      </div>
      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table fx-rows-interactive">
          <thead><tr><th>Name</th><th>Email</th><th>Subject</th><th>Message</th></tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={4} className="p-0">
                <div className="fx-empty" style={{ border: "none", borderRadius: 0 }}>
                  <div className="fx-empty-icon">✉️</div>
                  <h4>Inbox is clear</h4>
                  <p>General inquiries from the /contact form land here. New submissions also trigger an internal notification email so you never miss one.</p>
                </div>
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id}>
                <td className="font-semibold">{c.full_name}</td>
                <td>{c.email}</td>
                <td>{c.subject || "—"}</td>
                <td className="max-w-md truncate">{c.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};
